import {
  CATEGORY_LABELS,
  DEFAULT_CATEGORY_ORDER,
  type ShoppingCategory,
} from "@shared/categories";
import { createFileRoute } from "@tanstack/react-router";
import { format, parse } from "date-fns";
import { useLiveQuery } from "dexie-react-hooks";
import { AnimatePresence } from "framer-motion";
import { ArrowLeftRight } from "lucide-react";
import { useMemo, useState } from "react";
import { ItemIcon } from "@/components/ItemIcon";
import { ShoppingItemDetail } from "@/components/ShoppingItemDetail";
import { Spinner } from "@/components/Spinner";
import { alternativeSiblings } from "@/lib/alternatives";
import { db, type Recipe, type ShoppingItem } from "@/lib/db";
import { formatItemLabel } from "@/lib/item-label";
import { useShoppingLayout } from "@/lib/shopping-layout";
import { removeShoppingItem } from "@/lib/sync/change-tracker";
import { useLongPress } from "@/lib/use-long-press";

/** Muted badge marking an item that is one of an alternative group. */
function ChoiceBadge({ className }: { className?: string }) {
  return (
    <span className={className}>
      <ArrowLeftRight aria-hidden="true" className="size-3" />
      <span className="sr-only">Alternative available</span>
    </span>
  );
}

function ShoppingItemTile({
  item,
  isChoice,
  onRemove,
  onOpenDetail,
}: {
  item: ShoppingItem;
  isChoice: boolean;
  onRemove: (item: ShoppingItem) => void;
  onOpenDetail: (item: ShoppingItem, rect: DOMRect) => void;
}) {
  const { handlers, didLongPress } = useLongPress({
    onLongPress: (target) => {
      onOpenDetail(item, target.getBoundingClientRect());
    },
  });

  return (
    // -webkit-touch-callout:none stops iOS's long-press image callout (the zoom
    // preview + Share/Save menu) firing on the food icon before our own
    // long-press handler opens the detail view.
    <button
      type="button"
      className="relative flex w-full cursor-pointer select-none flex-col items-center justify-center gap-1 rounded-md border bg-card p-1.5 text-center text-card-foreground [-webkit-touch-callout:none] active:bg-muted"
      {...handlers}
      onClick={() => {
        // Suppress the tap-to-remove click that follows a long press; keyboard
        // (Enter/Space) activation has no long press, so it still removes.
        if (didLongPress()) return;
        onRemove(item);
      }}
    >
      {/* Bottom-right corner: the top corners belong to ItemIcon's date and
          frozen badges (anchored to the tile, its nearest positioned ancestor). */}
      {isChoice && (
        <ChoiceBadge className="absolute right-1 bottom-1 text-muted-foreground" />
      )}
      <ItemIcon
        groupAs={item.groupAs}
        date={item.date}
        category={item.category}
      />
      <span className="line-clamp-2 h-[2lh] w-full wrap-break-word text-xs leading-tight">
        {formatItemLabel(item)}
      </span>
    </button>
  );
}

function ShoppingItemRow({
  item,
  recipeTitle,
  isChoice,
  onRemove,
  onOpenDetail,
}: {
  item: ShoppingItem;
  recipeTitle?: string;
  isChoice: boolean;
  onRemove: (item: ShoppingItem) => void;
  onOpenDetail: (item: ShoppingItem, rect: DOMRect) => void;
}) {
  const { handlers, didLongPress } = useLongPress({
    onLongPress: (target) => {
      onOpenDetail(item, target.getBoundingClientRect());
    },
  });

  const parsedDate = item.date
    ? parse(item.date, "yyyy-MM-dd", new Date())
    : undefined;

  return (
    <button
      type="button"
      className="relative flex w-full cursor-pointer select-none items-center gap-3 rounded-md border bg-card p-2 text-left text-card-foreground [-webkit-touch-callout:none] active:bg-muted"
      {...handlers}
      onClick={() => {
        // Suppress the tap-to-remove click that follows a long press; keyboard
        // (Enter/Space) activation has no long press, so it still removes.
        if (didLongPress()) return;
        onRemove(item);
      }}
    >
      {/* Positioned wrapper so ItemIcon's frozen badge anchors to the icon, not
          the whole row (it uses `absolute` against the nearest ancestor). The
          date is shown right-justified below instead of as the icon's corner
          badge (which would overflow the small icon and duplicate the date). */}
      <span className="relative shrink-0">
        <ItemIcon groupAs={item.groupAs} category={item.category} size="sm" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm leading-tight">
          {formatItemLabel(item)}
        </span>
        {recipeTitle && (
          <span className="truncate text-xs leading-tight text-muted-foreground">
            {recipeTitle}
          </span>
        )}
      </span>
      {isChoice && <ChoiceBadge className="shrink-0 text-muted-foreground" />}
      {parsedDate && (
        <span className="shrink-0 text-xs font-medium text-muted-foreground tabular-nums">
          {format(parsedDate, "d MMM")}
        </span>
      )}
    </button>
  );
}

function ShoppingPage() {
  const [selected, setSelected] = useState<{
    item: ShoppingItem;
    rect: DOMRect;
  } | null>(null);
  const layout = useShoppingLayout();
  const items = useLiveQuery(() => db.shoppingItems.toArray());
  const settings = useLiveQuery(() =>
    db.userSettings.where("syncId").equals("user-settings-singleton").first(),
  );
  // Items only carry `recipeSyncId`, so the list-view subtitle and the
  // alternative-choice badge resolve the source recipe from this map (both
  // layouts need it for the badge).
  const recipes = useLiveQuery(() => db.recipes.toArray());
  const recipesBySyncId = useMemo(() => {
    const map = new Map<string, Recipe>();
    for (const recipe of recipes ?? []) map.set(recipe.syncId, recipe);
    return map;
  }, [recipes]);
  // An item is a "choice" when its source recipe ingredient belongs to an
  // alternative group ("X or Y" line) with at least one other member.
  const isChoice = (item: ShoppingItem) =>
    !!alternativeSiblings(
      item.recipeSyncId ? recipesBySyncId.get(item.recipeSyncId) : undefined,
      item.ingredientKey,
    );

  const groupedItems = useMemo(() => {
    if (!items) return null;
    const order = settings?.categoryOrder ?? DEFAULT_CATEGORY_ORDER;

    const groups = new Map<ShoppingCategory, ShoppingItem[]>();
    for (const item of items) {
      const key: ShoppingCategory = item.category ?? "other";
      const list = groups.get(key);
      if (list) {
        list.push(item);
      } else {
        groups.set(key, [item]);
      }
    }

    for (const list of groups.values()) {
      list.sort((a, b) => {
        // Primary: cluster by food grouping. groupAs is an optional taxonomy
        // path (manual / not-yet-categorised items lack it); joining it
        // general→specific nests families together under lexical sort. Fall
        // back to the display label when absent.
        const keyA =
          Array.isArray(a.groupAs) && a.groupAs.length
            ? a.groupAs.join("/")
            : formatItemLabel(a);
        const keyB =
          Array.isArray(b.groupAs) && b.groupAs.length
            ? b.groupAs.join("/")
            : formatItemLabel(b);
        const byGroup = keyA.localeCompare(keyB);
        if (byGroup !== 0) return byGroup;
        // Secondary: within a food group, the item needed soonest comes first.
        // `date` is yyyy-MM-dd (the meal-plan date), so a lexical compare is
        // chronological; undated items (manual adds, no meal-plan deadline)
        // sort after dated ones.
        if (a.date !== b.date) {
          if (!a.date) return 1;
          if (!b.date) return -1;
          return a.date.localeCompare(b.date);
        }
        // Tertiary: stable, readable order among items sharing group and date.
        return formatItemLabel(a).localeCompare(formatItemLabel(b));
      });
    }

    return order
      .filter((cat) => groups.has(cat))
      .map((cat) => ({
        category: cat,
        items: groups.get(cat) as ShoppingItem[],
      }));
  }, [items, settings]);

  if (!groupedItems) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    // pb clears the collapsed AddItemDrawer, which overlaps the scroll area by
    // ~(150px drawer − nav height) so the last row isn't hidden behind it
    <div className="space-y-4 pb-24">
      {groupedItems.map(({ category, items }) => (
        <section key={category}>
          <h2 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {CATEGORY_LABELS[category]}
          </h2>
          {layout === "list" ? (
            <ul className="flex flex-col gap-2">
              {items.map((item) => (
                <li key={item.id}>
                  <ShoppingItemRow
                    item={item}
                    recipeTitle={
                      item.recipeSyncId
                        ? recipesBySyncId.get(item.recipeSyncId)?.title
                        : undefined
                    }
                    isChoice={isChoice(item)}
                    onRemove={removeShoppingItem}
                    onOpenDetail={(item, rect) => setSelected({ item, rect })}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <ul className="grid grid-cols-[repeat(auto-fill,minmax(6.5rem,1fr))] gap-3">
              {items.map((item) => (
                <li key={item.id}>
                  <ShoppingItemTile
                    item={item}
                    isChoice={isChoice(item)}
                    onRemove={removeShoppingItem}
                    onOpenDetail={(item, rect) => setSelected({ item, rect })}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
      <AnimatePresence>
        {selected && (
          <ShoppingItemDetail
            item={selected.item}
            originRect={selected.rect}
            onClose={() => setSelected(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export const Route = createFileRoute("/_app/_shell/shopping")({
  component: ShoppingPage,
});
